<script setup>
    /**
     * Vue Scripts
     */
    import {onMounted, ref} from "vue";
    import {getQueryParams, searchCall} from "@util/api";

    /**
     * Vue Components
     *
     * @link https://vue-multiselect.js.org/
     */
    import Multiselect from 'vue-multiselect'

    const props = defineProps({
        api: {
            type: String,
            default: ''
        },
        closeOnSelect: {
            type: Boolean,
            default: false,
        },
        clearOnSelect: {
            type: Boolean,
            default: false,
        },
        fieldName: {
            type: String,
            default: 's'
        },
        id: {
            type: String,
            default: ''
        },
        inputId: String,
        isHcps: {
            type: Boolean,
            default: false,
        },
        label: String,
        loadFromQuery: Boolean,
        noResults: String,
        placeholder: String,
    })

    const emit = defineEmits(['getResult'])

    const loading = ref(false);
    const searchText = ref({name: '', url: ''});
    const results = ref([]);

    const {langCode} = GLOBAL

    onMounted(() => {
        if (props.loadFromQuery) {
            const {search} = getQueryParams(new URL(window.location).search) || ''
            if (search) {
                searchText.value.name = search.toString()
                searchText.value.url = '';
            }
        }
    })

    /**
     * Begins and API call where we send the search value to
     * WordPress' Rest API to gather a list of results
     *
     * @param search
     * @returns {Promise<void>}
     */
    const startSearch = async (search) => {
        if (search && search.length > 2) {
            loading.value = true;
            searchText.value.name = search;
            let searchResults = [];

            await searchCall(props.api, search, {
                hitsPerPage: 3,
                lang: langCode,
                isHcps: props.isHcps,
            })
                .then(results => results.length && (searchResults.push(...results)))
                .catch((err) => console.error(err))
                .finally(() => loading.value = false);

            results.value = [
                {
                    ID: 0,
                    name: search || '',
                    value: search || '',
                    url: '',
                },
                ...searchResults
            ]
        }

        /**
         * Reset the search result to catch for full
         * deletion or further refinement of the query
         */
        if (search.length < 3) {
            results.value = []
            searchText.value = {name: '', url: ''};
        }

        loading.value = false;
    }

    const valueSelected = (data) => {
        searchText.value.name = data.name;
        emit('getResult', data)
    }

    const limitText = (count) => `+${count}`

</script>

<template>
    <Multiselect
        :model-value="searchText"

        :allow-empty="true"
        :clear-on-select="clearOnSelect"
        :close-on-select="closeOnSelect"
        :hide-selected="false"
        :id="`${id}-ajax`"
        :internal-search="false"
        :loading="loading"
        :limit="3"
        :limit-text="limitText"
        :max-height="600"
        :multiple="false"
        :options="results"
        :options-limit="10"
        :preserveSearch="true"
        :preselect-first="false"
        :placeholder="placeholder"
        :searchable="true"
        :show-no-options="false"
        :show-no-results="false"
        :value="searchText?.name || placeholder"

        label="name"
        name="name"
        open-direction="bottom"

        class="vue-multiselect-predictive-search"

        @keydown.enter="valueSelected(searchText)"
        @search-change="startSearch"
        @select="valueSelected"
    >

        <template #singleLabel="{option}">
            <p
                class="multiselect__tags-value"
                v-html="option?.name || placeholder"
            />
        </template>

        <!-- The results -->
        <template #option="{option}">
            <p
                class="dropdown__results-item-text"
                v-html="option.name"
            />
        </template>

<!--        <template #selection="{value, search, isOpen}">-->
<!--           <div-->
<!--               v-if="search"-->
<!--               v-show="!isOpen"-->
<!--               class="multiselect__search"-->
<!--           >-->
<!--               <p-->
<!--                   class="multiselect__search-text"-->
<!--                   v-html="search"-->
<!--               />-->
<!--           </div>-->
<!--        </template>-->

        <template #caret></template>

    </Multiselect>
</template>

<script>
    import "vue-multiselect/dist/vue-multiselect.css"

    export default {
        name: "Predictive Search Box"
    }
</script>

<style lang="scss">
    .vue-multiselect-predictive-search {
        .multiselect__select {
            display: none;
        }
    }
</style>
